import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './InteractiveMap.css';

interface SpawnData {
  Pokemon: string;
  MinLVL: number;
  MaxLVL: number;
  Tier: string;
  MemberOnly: boolean;
  Map: string;
  Daytime?: number[];
  Item?: string;
}

interface MapRegion {
  name: string;
  coords: string;
  shape: 'rect' | 'poly' | 'circle';
  subzones: string[] | null;
  groupName?: string;
  groupColor?: string;
}

interface RegionGroup {
  color: string;
  regions: MapRegion[];
}

interface MapRegionsData {
  [key: string]: RegionGroup;
}

// Utility functions for daytime filtering
const formatDaytime = (daytimeArray: number[] | undefined): string => {
  if (!daytimeArray || daytimeArray.length === 0) return 'M/D/N';

  // Handle single value format first
  if (daytimeArray.length === 1) {
    const timeMap: { [key: number]: string } = {
      1: 'M/D/N',
      2: 'N',
      3: 'M/D'
    };
    return timeMap[daytimeArray[0]] || 'M/D/N';
  }

  // Handle array format [morning, day, night]
  if (daytimeArray.length === 3) {
    const [morning, day, night] = daytimeArray;
    const times = [];

    if (morning === 1) times.push('M');
    if (day === 1) times.push('D');
    if (night === 1) times.push('N');

    return times.length > 0 ? times.join('/') : 'M/D/N';
  }

  return 'M/D/N';
};

const matchesDaytime = (spawn: SpawnData, selectedTime: 'all' | 'morning' | 'day' | 'night'): boolean => {
  if (selectedTime === 'all') return true;

  const daytime = spawn.Daytime;
  if (!daytime || daytime.length === 0) return true; // If no daytime data, show in all times

  // Handle different daytime formats
  if (daytime.length === 1) {
    // Single value format: 1 = all times, 2 = night only, 3 = morning/day only
    switch (daytime[0]) {
      case 1: return true; // All times
      case 2: return selectedTime === 'night'; // Night only
      case 3: return selectedTime === 'morning' || selectedTime === 'day'; // Morning/Day only
      default: return true;
    }
  } else if (daytime.length === 3) {
    // Array format: [morning, day, night] where 1 = available, 0 = not available
    switch (selectedTime) {
      case 'morning': return daytime[0] === 1;
      case 'day': return daytime[1] === 1;
      case 'night': return daytime[2] === 1;
      default: return true;
    }
  }

  return true; // Default to showing if format is unclear
};

// Tier weights for spawn calculation (from Discord bot)
const TIER_WEIGHTS: { [key: string]: number } = {
  'Rare': 2,      // 2%
  'Uncommon': 18, // 18%
  'Common': 80    // 80%
};

const calculateSpawnRates = (spawns: SpawnData[]): Array<SpawnData & { spawnRate: number }> => {
  if (spawns.length === 0) return [];

  // Calculate total weight of all Pokemon in this spawn data
  let totalWeight = 0;
  spawns.forEach(spawn => {
    totalWeight += (TIER_WEIGHTS[spawn.Tier] || 1);
  });

  // Group by Pokemon name and calculate their combined weight
  const pokemonWeights = spawns.reduce((acc, spawn) => {
    if (!acc[spawn.Pokemon]) {
      acc[spawn.Pokemon] = 0;
    }
    acc[spawn.Pokemon] += (TIER_WEIGHTS[spawn.Tier] || 1);
    return acc;
  }, {} as Record<string, number>);

  // Calculate rates for each spawn
  const results: Array<SpawnData & { spawnRate: number }> = [];

  spawns.forEach(spawn => {
    const pokemonWeight = pokemonWeights[spawn.Pokemon];
    const spawnRate = totalWeight > 0 ? (pokemonWeight / totalWeight * 100) : 0;

    results.push({
      ...spawn,
      spawnRate: spawnRate
    });
  });

  // Sort by spawn rate (highest first) then by Pokemon name
  results.sort((a, b) => {
    if (b.spawnRate !== a.spawnRate) {
      return b.spawnRate - a.spawnRate;
    }
    return a.Pokemon.localeCompare(b.Pokemon);
  });

  return results;
};



const InteractiveMap: React.FC = () => {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedSubzone, setSelectedSubzone] = useState<string | null>(null);
  const [availableSubzones, setAvailableSubzones] = useState<string[] | null>(null);
  const [selectedDaytime, setSelectedDaytime] = useState<'all' | 'morning' | 'day' | 'night'>('all');
  const [selectedSpawnType, setSelectedSpawnType] = useState<'all' | 'land' | 'surf'>('all');
  const [spawnData, setSpawnData] = useState<SpawnData[]>([]);
  const [filteredSpawnData, setFilteredSpawnData] = useState<SpawnData[]>([]);
  const [spawnTypeOptions, setSpawnTypeOptions] = useState<{ hasLand: boolean; hasSurf: boolean }>({ hasLand: false, hasSurf: false });
  const [loading, setLoading] = useState(false);
  const [regionsData, setRegionsData] = useState<MapRegionsData>({});
  const [flatRegions, setFlatRegions] = useState<MapRegion[]>([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [minScale, setMinScale] = useState(0.5);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [debugCoords, setDebugCoords] = useState({ x: 0, y: 0 });
  const [showDebug, setShowDebug] = useState(true); // Set to false to hide debug overlay

  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MapRegion[]>([]);
  const [highlightedRegions, setHighlightedRegions] = useState<string[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightFromSearch, setHighlightFromSearch] = useState(false);

  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });

  // Load regions from JSON file
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const response = await fetch('/mapRegions.json');
        if (response.ok) {
          const loadedRegions: MapRegionsData = await response.json();
          setRegionsData(loadedRegions);

          // Flatten regions for easier use in map areas
          const flattened: MapRegion[] = [];
          Object.entries(loadedRegions).forEach(([groupName, group]) => {
            group.regions.forEach(region => {
              flattened.push({
                ...region,
                // Store the group info for color mapping
                groupName,
                groupColor: group.color
              } as MapRegion & { groupName: string; groupColor: string });
            });
          });
          setFlatRegions(flattened);
        } else {
          console.error('Failed to load mapRegions.json file');
          setRegionsData({});
          setFlatRegions([]);
        }
      } catch (error) {
        console.error('Failed to load regions:', error);
        setRegionsData({});
        setFlatRegions([]);
      }
    };

    loadRegions();
  }, []);

  // Filter spawn data based on selected daytime and spawn type
  useEffect(() => {
    if (spawnData.length > 0) {
      // Check what spawn types are available
      const hasLand = spawnData.some(spawn => (spawn as any)._source === 'land');
      const hasSurf = spawnData.some(spawn => (spawn as any)._source === 'surf');
      setSpawnTypeOptions({ hasLand, hasSurf });

      // Filter by daytime
      let filtered = spawnData.filter(spawn => matchesDaytime(spawn, selectedDaytime));

      // Filter by spawn type
      if (selectedSpawnType !== 'all') {
        filtered = filtered.filter(spawn => {
          const source = (spawn as any)._source;
          return source === selectedSpawnType;
        });
      }

      const withRates = calculateSpawnRates(filtered);
      setFilteredSpawnData(withRates);
    } else {
      setFilteredSpawnData([]);
      setSpawnTypeOptions({ hasLand: false, hasSurf: false });
    }
  }, [spawnData, selectedDaytime, selectedSpawnType]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Focus search on Ctrl+F or just '/' key
      if ((event.ctrlKey && event.key === 'f') || event.key === '/') {
        event.preventDefault();
        const searchInput = document.querySelector('.search-container input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      // Enter key selects first search result
      if (event.key === 'Enter' && searchResults.length > 0 && showSearchResults) {
        event.preventDefault();
        zoomToRegion(searchResults[0]);
      }
      // Escape key clears search and highlights
      if (event.key === 'Escape') {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
        clearHighlights();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clear highlights when search is cleared
  const clearHighlights = () => {
    setHighlightedRegions([]);
    setHighlightFromSearch(false);
  };

  // Prevent page scrolling when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Calculate minimum scale to fit image in viewport
  useEffect(() => {
    const calculateMinScale = () => {
      const container = document.querySelector('.map-zoom-container') as HTMLElement;
      const image = document.querySelector('.world-map') as HTMLImageElement;

      if (!container || !image || !image.naturalWidth) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imageWidth = image.naturalWidth;
      const imageHeight = image.naturalHeight;

      // Calculate scale needed to fit image in container
      const scaleX = containerWidth / imageWidth;
      const scaleY = containerHeight / imageHeight;
      const minRequiredScale = Math.max(scaleX, scaleY);

      setMinScale(minRequiredScale);

      // If current scale is below minimum, reset it
      if (scale < minRequiredScale) {
        setScale(minRequiredScale);
        setPanOffset({ x: 0, y: 0 });
      }
    };

    // Calculate on mount and when window resizes
    const handleResize = () => calculateMinScale();
    window.addEventListener('resize', handleResize);

    // Also calculate when image loads
    const image = document.querySelector('.world-map') as HTMLImageElement;
    if (image) {
      if (image.complete) {
        calculateMinScale();
      } else {
        image.onload = calculateMinScale;
      }
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [scale]);

  // Fetch spawn data for selected region or subzone
  const fetchSpawnData = async (mapName: string) => {
    setLoading(true);
    try {
      // Use the backend API to search spawns — avoids downloading 1.8 MB of raw JSON
      const response = await fetch('/api/spawns/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: mapName, type: 'map' }),
        cache: 'no-store',
      });

      if (!response.ok) throw new Error(`Spawn API returned ${response.status}`);

      const data = await response.json();
      // Backend already tags spawns with _source: 'land' | 'surf'
      const filteredSpawns = (data.spawns as SpawnData[]).filter((spawn) =>
        spawn.Map && spawn.Map.toLowerCase() === mapName.toLowerCase()
      );
      setSpawnData(filteredSpawns || []);
    } catch (error) {
      console.error('Failed to fetch spawn data:', error);
      setSpawnData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRegionHover = (regionName: string, event: React.MouseEvent) => {
    setHoveredRegion(regionName);
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleRegionLeave = () => {
    setHoveredRegion(null);
  };

  const handleRegionClick = (regionName: string) => {
    // Find the clicked region data
    const regionData = flatRegions.find(r => r.name === regionName);

    setSelectedRegion(regionName);

    if (regionData && regionData.subzones && regionData.subzones.length > 0) {
      // Region has subzones - set them up for selection
      setAvailableSubzones(regionData.subzones);
      setSelectedSubzone(null); // Reset subzone selection
      // Initially fetch data for the main region
      fetchSpawnData(regionName);
    } else {
      // No subzones - fetch data for the main region directly
      setAvailableSubzones(null);
      setSelectedSubzone(null);
      fetchSpawnData(regionName);
    }

    // Clear highlights when clicking on a region normally
    if (!highlightedRegions.includes(regionName)) {
      setHighlightedRegions([]);
    }
  };

  const handleSubzoneSelect = (subzoneName: string) => {
    setSelectedSubzone(subzoneName);
    fetchSpawnData(subzoneName);
  };

  const handleMainRegionSelect = () => {
    setSelectedSubzone(null);
    if (selectedRegion) {
      fetchSpawnData(selectedRegion);
    }
  };

  const handleDaytimeSelect = (daytime: 'all' | 'morning' | 'day' | 'night') => {
    setSelectedDaytime(daytime);
  };

  const handleSpawnTypeSelect = (spawnType: 'all' | 'land' | 'surf') => {
    setSelectedSpawnType(spawnType);
  };

  const closePopup = () => {
    setSelectedRegion(null);
    setSelectedSubzone(null);
    setAvailableSubzones(null);
    setSelectedDaytime('all');
    setSelectedSpawnType('all');
    setSpawnData([]);
    setFilteredSpawnData([]);
    setSpawnTypeOptions({ hasLand: false, hasSurf: false });
    clearHighlights();
  };

  const navigate = useNavigate();

  const handleBackToSite = () => {
    navigate('/');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Debug function to track mouse coordinates relative to the image
  const handleDebugMouseMove = (e: React.MouseEvent) => {
    const image = e.currentTarget.querySelector('.world-map') as HTMLImageElement;
    if (!image) return;

    const rect = image.getBoundingClientRect();
    const imageX = e.clientX - rect.left;
    const imageY = e.clientY - rect.top;

    // Calculate coordinates relative to the original image size (accounting for scale and pan)
    const scaleAdjustedX = imageX / scale;
    const scaleAdjustedY = imageY / scale;

    setDebugCoords({
      x: Math.round(scaleAdjustedX),
      y: Math.round(scaleAdjustedY)
    });
  };

  const getRegionColor = (regionName: string) => {
    const region = flatRegions.find((r: MapRegion & { groupColor?: string }) => r.name === regionName);
    return (region as MapRegion & { groupColor?: string })?.groupColor || '#feca57';
  };

  // Search functionality with debouncing
  const performSearch = (query: string) => {
    if (query.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      clearHighlights();
      return;
    }

    // Search through all regions and their subzones
    const results: MapRegion[] = [];
    const searchTerm = query.toLowerCase();

    flatRegions.forEach(region => {
      // Search by region name (prioritize exact matches)
      const nameMatch = region.name.toLowerCase();
      const isExactMatch = nameMatch === searchTerm;
      const isPartialMatch = nameMatch.includes(searchTerm);

      if (isExactMatch || isPartialMatch) {
        // Add exact matches first
        if (isExactMatch) {
          results.unshift(region);
        } else {
          results.push(region);
        }
        return;
      }

      // Search by subzones if they exist
      if (region.subzones && Array.isArray(region.subzones)) {
        const hasSubzoneMatch = region.subzones.some(subzone =>
          subzone.toLowerCase().includes(searchTerm)
        );
        if (hasSubzoneMatch && !results.some(r => r.name === region.name)) {
          results.push(region);
        }
      }

      // Search by group name
      if (region.groupName && region.groupName.toLowerCase().includes(searchTerm)) {
        if (!results.some(r => r.name === region.name)) {
          results.push(region);
        }
      }
    });

    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      // Only clear highlights if they weren't set by clicking a search result
      if (!highlightFromSearch) {
        setHighlightedRegions([]);
      }
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, flatRegions]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const zoomToRegion = (region: MapRegion) => {
    const coords = region.coords.split(',').map(Number);
    if (coords.length !== 4) return;

    const [x1, y1, x2, y2] = coords;
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;

    // Determine target scale based on region size
    const maxDimension = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1));
    let targetScale = 2.5;
    if (maxDimension < 50) targetScale = 4;
    else if (maxDimension < 100) targetScale = 3;
    else if (maxDimension > 200) targetScale = 1.8;

    // With transformOrigin: center center on a wrapper centered by flex:
    // screenX of image point (ix) = containerCX + panX + (ix - naturalW/2) * scale
    // For (ix) to land at containerCX: panX = (naturalW/2 - ix) * scale
    const image = imgRef.current;
    if (!image?.naturalWidth) return;

    const panX = (image.naturalWidth  / 2 - centerX) * targetScale;
    const panY = (image.naturalHeight / 2 - centerY) * targetScale;

    const constrained = constrainPan(panX, panY, targetScale);
    setScale(targetScale);
    setPanOffset(constrained);
    setHighlightedRegions([region.name]);
    setHighlightFromSearch(true);
    setShowSearchResults(false);
  };

  const handleSearchResultClick = (region: MapRegion) => {
    zoomToRegion(region);
  };

  // Zoom functions
  const handleZoomIn = () => {
    const newScale = Math.min(scale * 1.2, 5);
    setScale(newScale);
    // Re-constrain pan offset after zoom change
    const constrained = constrainPan(panOffset.x, panOffset.y, newScale);
    setPanOffset(constrained);
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale / 1.2, minScale);
    setScale(newScale);
    // Re-constrain pan offset after zoom change
    const constrained = constrainPan(panOffset.x, panOffset.y, newScale);
    setPanOffset(constrained);
  };

  // Helper function to constrain pan within image bounds
  const constrainPan = (x: number, y: number, currentScale: number) => {
    const container = document.querySelector('.map-zoom-container') as HTMLElement;
    const image = document.querySelector('.world-map') as HTMLImageElement;

    if (!container || !image || !image.naturalWidth) return { x: 0, y: 0 };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const imageWidth = image.naturalWidth * currentScale;
    const imageHeight = image.naturalHeight * currentScale;

    // If image is smaller than container, center it (no panning allowed)
    if (imageWidth <= containerWidth && imageHeight <= containerHeight) {
      return { x: 0, y: 0 };
    }

    // Calculate maximum pan distances to keep image edges within container
    let maxPanX = 0;
    let maxPanY = 0;

    if (imageWidth > containerWidth) {
      maxPanX = (imageWidth - containerWidth) / 2;
    }

    if (imageHeight > containerHeight) {
      maxPanY = (imageHeight - containerHeight) / 2;
    }

    // Constrain the pan values
    const constrainedX = Math.max(-maxPanX, Math.min(maxPanX, x));
    const constrainedY = Math.max(-maxPanY, Math.min(maxPanY, y));

    return { x: constrainedX, y: constrainedY };
  };

  // Pan functions
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'IMG') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const newX = e.clientX - panStart.x;
      const newY = e.clientY - panStart.y;
      const constrained = constrainPan(newX, newY, scale);
      setPanOffset(constrained);
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsPanning(true);
      setPanStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isPanning && e.touches.length === 1) {
      const touch = e.touches[0];
      const newX = touch.clientX - panStart.x;
      const newY = touch.clientY - panStart.y;
      const constrained = constrainPan(newX, newY, scale);
      setPanOffset(constrained);
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
  };

  // Wheel zoom — must use imperative addEventListener with {passive: false}
  // because React synthetic onWheel is passive in modern browsers,
  // which prevents e.preventDefault() from working (causes console warnings).
  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => {
      const newScale = Math.max(minScale, Math.min(5, prev * delta));
      setPanOffset(offset => constrainPan(offset.x, offset.y, newScale));
      return newScale;
    });
  };

  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  });

  return (
    <div className="interactive-map-container">
      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.02);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .search-container input:focus {
            transform: translateY(-1px);
          }

          .search-results-item:hover {
            transform: translateX(4px);
          }

          kbd {
            background-color: #f1f1f1;
            border: 1px solid #ccc;
            border-radius: 3px;
            box-shadow: 0 1px 0 rgba(0,0,0,0.2), 0 0 0 1px #fff inset;
            color: #333;
            display: inline-block;
            font-family: monospace;
            font-size: 0.85em;
            font-weight: 700;
            line-height: 1;
            padding: 2px 4px;
            white-space: nowrap;
          }
        `}
      </style>
      {/* Sidebar toggle button */}
      <button
        className={`sidebar-toggle ${sidebarOpen ? 'open' : ''}`}
        onClick={toggleSidebar}
      >
        {sidebarOpen ? '←' : '→'}
      </button>

      {/* Left sidebar navigation */}
      <div className={`sidebar-nav ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <div className="sidebar-title">PRO Interactive Map</div>
            <div className="sidebar-subtitle">Pokemon Revolution Online</div>
          </div>

          <div className="sidebar-section">
            <h3>Navigation</h3>
            <button className="back-btn" onClick={handleBackToSite}>
              ← Back to PRO Market History
            </button>
          </div>

          <div className="sidebar-section">
            <h3>Instructions</h3>
            <div className="sidebar-info">
              • Use the search bar to find regions, areas, or subzones<br/>
              • Press <kbd>/</kbd> or <kbd>Ctrl+F</kbd> to focus search bar<br/>
              • Press <kbd>Esc</kbd> to clear search and highlights<br/>
              • Click on any region to see Pokemon spawns<br/>
              • Use mouse wheel to zoom in/out<br/>
              • Click and drag to pan around<br/>
              • Use zoom buttons (+/-) on the right<br/>
              • Touch gestures work on mobile devices<br/>
              • Search results will auto-zoom and highlight regions in red
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Debug Tools</h3>
            <button
              className="back-btn"
              onClick={() => setShowDebug(!showDebug)}
              style={{ marginBottom: '10px' }}
            >
              {showDebug ? 'Hide' : 'Show'} Coordinates
            </button>
            <div className="sidebar-info">
              Toggle coordinate display to help map clickable regions.
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Regions</h3>
            <div className="sidebar-info">
              Explore Kanto, Johto, Hoenn, and Sinnoh regions. Click on cities, routes, and special areas to discover Pokemon spawns, levels, and rarities.
            </div>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="search-container" style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          {/* Search icon */}
          <div style={{
            position: 'absolute',
            left: '16px',
            zIndex: 1003,
            color: '#999',
            pointerEvents: 'none'
          }}>
            🔍
          </div>

          <input
            type="text"
            placeholder="Search regions, areas, or subzones..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              padding: '12px 20px 12px 44px',
              fontSize: '14px',
              borderRadius: '25px',
              border: '2px solid #ddd',
              background: 'rgba(255, 255, 255, 0.95)',
              minWidth: '300px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#4CAF50';
              e.target.style.boxShadow = '0 4px 16px rgba(76,175,80,0.3)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ddd';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
          />

          {/* Loading indicator or clear button */}
          <div style={{
            position: 'absolute',
            right: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {isSearching && (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ddd',
                borderTop: '2px solid #4CAF50',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {searchQuery && !isSearching && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowSearchResults(false);
                  clearHighlights();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#999',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0f0f0';
                  e.currentTarget.style.color = '#666';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#999';
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Search results dropdown */}
        {showSearchResults && searchResults.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            marginTop: '8px',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 1002,
            border: '1px solid #e0e0e0'
          }}>
            {searchResults.slice(0, 8).map((region, index) => (
              <div
                key={`${region.name}-${index}`}
                onClick={() => handleSearchResultClick(region)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: index < Math.min(searchResults.length, 8) - 1 ? '1px solid #f0f0f0' : 'none',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div style={{
                  fontWeight: '500',
                  color: '#333',
                  fontSize: '14px'
                }}>
                  {region.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    background: region.groupColor || '#feca57',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: '500'
                  }}>
                    {region.groupName}
                  </span>
                  {region.subzones && region.subzones.length > 0 && (
                    <span style={{ color: '#888' }}>
                      • {region.subzones.length} subzone{region.subzones.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {searchResults.length > 8 && (
              <div style={{
                padding: '8px 16px',
                fontSize: '12px',
                color: '#888',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                +{searchResults.length - 8} more results...
              </div>
            )}
          </div>
        )}

        {showSearchResults && searchResults.length === 0 && searchQuery.trim() !== '' && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            marginTop: '8px',
            padding: '16px',
            textAlign: 'center',
            color: '#888',
            fontSize: '14px',
            border: '1px solid #e0e0e0'
          }}>
            No regions found for "{searchQuery}"
          </div>
        )}
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={handleZoomIn}>+</button>
        <button className="zoom-btn" onClick={handleZoomOut}>−</button>
      </div>

      <div className="map-wrapper">
        <div className="map-image-container">
          <div
            className="map-zoom-container"
            onMouseDown={handleMouseDown}
            onMouseMove={(e) => {
              handleMouseMove(e);
              handleDebugMouseMove(e);
            }}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            ref={zoomContainerRef}
            style={{
              cursor: isPanning ? 'grabbing' : 'grab'
            }}
          >
            {/*
              Single wrapper div that receives the pan+zoom transform.
              Both the image and the SVG highlight overlay live inside it,
              so they share the exact same coordinate origin and are always
              perfectly aligned — regardless of zoom level or pan position.
            */}
            <div
              style={{
                position: 'relative',
                lineHeight: 0,
                // flex-shrink:0 prevents the flex container from compressing this wrapper
                // below the image's natural size. Without it the wrapper shrinks and the
                // SVG viewBox would be in a different coordinate space than the image pixels.
                flexShrink: 0,
                width:  imgNaturalSize.w > 0 ? imgNaturalSize.w : undefined,
                height: imgNaturalSize.h > 0 ? imgNaturalSize.h : undefined,
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: 'transform 0.1s ease',
              }}
            >
              <img
                ref={imgRef}
                src="/images/pro_world_map.png"
                alt="Pokemon Revolution Online World Map"
                className="world-map"
                useMap="#pro-world-map"
                style={{ display: 'block' }}
                draggable={false}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  setImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
                }}
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
              />

              {/* SVG highlight overlay — coordinates are in image-pixel space,
                  exactly matching mapRegions.json. No math required. */}
              {highlightedRegions.length > 0 && imgNaturalSize.w > 0 && (
                <svg
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    overflow: 'visible',
                    zIndex: 10,
                  }}
                  width={imgNaturalSize.w}
                  height={imgNaturalSize.h}
                  viewBox={`0 0 ${imgNaturalSize.w} ${imgNaturalSize.h}`}
                >
                  {highlightedRegions.map((regionName) => {
                    const region = flatRegions.find(r => r.name === regionName);
                    if (!region?.coords || region.shape !== 'rect') return null;
                    const [x1, y1, x2, y2] = region.coords.split(',').map(Number);
                    const x = Math.min(x1, x2);
                    const y = Math.min(y1, y2);
                    const w = Math.abs(x2 - x1);
                    const h = Math.abs(y2 - y1);
                    return (
                      <g key={`highlight-${regionName}`}>
                        {/* subtle yellow fill */}
                        <rect
                          x={x} y={y} width={w} height={h}
                          fill="rgba(255, 220, 0, 0.15)"
                          stroke="none"
                        />
                        {/* black backing border for contrast */}
                        <rect
                          x={x} y={y} width={w} height={h}
                          fill="none"
                          stroke="#000000"
                          strokeWidth={Math.max(2, 4 / scale)}
                        />
                        {/* bright yellow solid outline on top */}
                        <rect
                          x={x} y={y} width={w} height={h}
                          fill="none"
                          stroke="#FFE600"
                          strokeWidth={Math.max(1, 2 / scale)}
                        />
                      </g>
                    );
                  })}
                </svg>
              )}

              <map name="pro-world-map">
                {flatRegions.map((region, index) => (
                  <area
                    key={`${region.name}-${index}`}
                    shape={region.shape}
                    coords={region.coords}
                    alt={region.name}
                    title={region.name}
                    onMouseEnter={(e) => handleRegionHover(region.name, e)}
                    onMouseLeave={handleRegionLeave}
                    onClick={() => handleRegionClick(region.name)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </map>
            </div>

            {/* Hover tooltip */}
            {hoveredRegion && (
              <div
                className="map-tooltip"
                style={{
                  position: 'fixed',
                  left: mousePosition.x + 10,
                  top: mousePosition.y - 10,
                  backgroundColor: getRegionColor(hoveredRegion),
                  color: 'white',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  pointerEvents: 'none',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  maxWidth: '200px',
                  lineHeight: '1.2',
                  wordWrap: 'break-word'
                }}
              >
                {hoveredRegion}
              </div>
            )}

            {/* Debug coordinates overlay */}
            {showDebug && (
              <div className="debug-overlay">
                <div className="debug-coords">
                  <strong>Debug Coordinates:</strong><br/>
                  X: {debugCoords.x}<br/>
                  Y: {debugCoords.y}<br/>
                  <small>Scale: {scale.toFixed(2)}x</small>
                </div>
                <div className="debug-toggle">
                  <button
                    onClick={() => setShowDebug(!showDebug)}
                    className="debug-toggle-btn"
                  >
                    Hide Debug
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spawn data popup */}
      {selectedRegion && (
        <div className="spawn-popup-overlay" onClick={closePopup}>
          <div className="spawn-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2>{selectedRegion}{selectedSubzone ? ` - ${selectedSubzone}` : ''}</h2>
              <button className="close-button" onClick={closePopup}>×</button>
            </div>

            {/* Subzone Selection */}
            {availableSubzones && availableSubzones.length > 0 && (
              <div className="subzone-selection">
                <h3>Select Area:</h3>
                <div className="subzone-buttons">
                  <button
                    onClick={handleMainRegionSelect}
                    className={`subzone-btn ${selectedSubzone === null ? 'active' : ''}`}
                  >
                    {selectedRegion} (Main)
                  </button>
                  {availableSubzones.map((subzone) => (
                    <button
                      key={subzone}
                      onClick={() => handleSubzoneSelect(subzone)}
                      className={`subzone-btn ${selectedSubzone === subzone ? 'active' : ''}`}
                    >
                      {subzone}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Daytime Toggle */}
            <div className="daytime-selection">
              <h3>Time of Day:</h3>
              <div className="daytime-buttons">
                <button
                  onClick={() => handleDaytimeSelect('all')}
                  className={`daytime-btn ${selectedDaytime === 'all' ? 'active' : ''}`}
                >
                  🌅 All Day
                </button>
                <button
                  onClick={() => handleDaytimeSelect('morning')}
                  className={`daytime-btn ${selectedDaytime === 'morning' ? 'active' : ''}`}
                >
                  🌅 Morning
                </button>
                <button
                  onClick={() => handleDaytimeSelect('day')}
                  className={`daytime-btn ${selectedDaytime === 'day' ? 'active' : ''}`}
                >
                  ☀️ Day
                </button>
                <button
                  onClick={() => handleDaytimeSelect('night')}
                  className={`daytime-btn ${selectedDaytime === 'night' ? 'active' : ''}`}
                >
                  🌙 Night
                </button>
              </div>
            </div>

            {/* Spawn Type Toggle (only show if both land and surf spawns exist) */}
            {spawnTypeOptions.hasLand && spawnTypeOptions.hasSurf && (
              <div className="spawn-type-selection">
                <h3>Spawn Type:</h3>
                <div className="spawn-type-buttons">
                  <button
                    onClick={() => handleSpawnTypeSelect('all')}
                    className={`spawn-type-btn ${selectedSpawnType === 'all' ? 'active' : ''}`}
                  >
                    🌍 All
                  </button>
                  <button
                    onClick={() => handleSpawnTypeSelect('land')}
                    className={`spawn-type-btn ${selectedSpawnType === 'land' ? 'active' : ''}`}
                  >
                    🌱 Land
                  </button>
                  <button
                    onClick={() => handleSpawnTypeSelect('surf')}
                    className={`spawn-type-btn ${selectedSpawnType === 'surf' ? 'active' : ''}`}
                  >
                    🌊 Surf
                  </button>
                </div>
              </div>
            )}

            <div className="popup-content">
              {loading ? (
                <div className="loading">Loading spawn data...</div>
              ) : filteredSpawnData.length > 0 ? (
                <div className="spawn-list">
                  <div className="spawn-summary">
                    <p>
                      <strong>{filteredSpawnData.length}</strong> Pokemon found
                      {(selectedDaytime !== 'all' || selectedSpawnType !== 'all') && spawnData.length > filteredSpawnData.length && (
                        <span className="filtered-note">
                          ({spawnData.length - filteredSpawnData.length} filtered by {
                            [
                              selectedDaytime !== 'all' && 'time',
                              selectedSpawnType !== 'all' && 'spawn type'
                            ].filter(Boolean).join(' and ')
                          })
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="spawn-grid">
                    {filteredSpawnData.map((spawn, index) => {
                      const source = (spawn as SpawnData & { _source?: string })._source;
                      const spawnWithRate = spawn as SpawnData & { spawnRate: number };
                      return (
                        <div key={index} className={`spawn-item ${spawn.Tier.toLowerCase()}`}>
                          <div className="pokemon-name">
                            {spawn.Pokemon}
                            {spawn.MemberOnly && <span className="ms-badge">MS</span>}
                            <span className="spawn-rate">{spawnWithRate.spawnRate.toFixed(1)}%</span>
                          </div>
                          <div className="pokemon-details">
                            <span className="level-range">Lv. {spawn.MinLVL}-{spawn.MaxLVL}</span>
                            <span className={`rarity ${spawn.Tier.toLowerCase()}`}>{spawn.Tier}</span>
                            <span className="daytime-badge">{formatDaytime(spawn.Daytime)}</span>
                            <span className="spawn-type-badge" style={{
                              background: source === 'surf' ? '#2196f3' : '#43a047',
                              color: '#fff',
                              borderRadius: '6px',
                              padding: '2px 8px',
                              fontSize: '0.85rem',
                              marginLeft: '8px',
                              fontWeight: 600
                            }}>
                              {source === 'surf' ? 'Surf' : 'Land'}
                            </span>
                          </div>
                          {spawn.Item && (
                            <div className="pokemon-item">Item: {spawn.Item}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="no-spawns">
                  {spawnData.length > 0 ? (
                    <>
                      <p>No Pokemon spawn during {selectedDaytime === 'all' ? 'any time' : selectedDaytime}.</p>
                      <p><small>Try selecting a different time of day above.</small></p>
                    </>
                  ) : (
                    <>
                      <p>No spawn data available for this region.</p>
                      <p><small>This might be a city or special area without wild Pokemon.</small></p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveMap;
